import { useEffect, useState } from 'react';
import { API_BASE_URL } from './api';

/**
 * Single source of truth for selectable models, served by the backend
 * (GET /api/models, backed by app/core/model_catalog.py). To add a model,
 * edit that Python registry, every dropdown that uses this hook updates
 * automatically, and billing stays in sync because it reads the same list.
 *
 * Each model: { id, label, provider, value, channels, input, output }
 *   - value    : provider-prefixed string stored by Web/VAPI (e.g. "openai:gpt-4o")
 *   - id        : bare name stored by Realtime and used as the billing key
 *   - channels : subset of ["web", "vapi", "realtime"]
 *   - input/output : USD per 1M tokens
 */

// Module-level cache so the catalog is fetched once per page load and shared
// across every dropdown, instead of refetching on each component mount.
let cataloguePromise = null;

export const fetchModelCatalog = () => {
  if (!cataloguePromise) {
    const token = localStorage.getItem('access_token');
    cataloguePromise = fetch(`${API_BASE_URL}/api/models`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load model catalog (${res.status})`);
        return res.json();
      })
      .then((data) => data.models || [])
      .catch((err) => {
        // Reset so a later mount can retry after a transient failure.
        cataloguePromise = null;
        throw err;
      });
  }
  return cataloguePromise;
};

/** React hook: returns { models, loading, error } filtered to a channel. */
export const useModelCatalog = (channel = null) => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    fetchModelCatalog()
      .then((all) => {
        if (!active) return;
        setModels(channel ? all.filter((m) => m.channels.includes(channel)) : all);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        setError(err.message);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [channel]);

  return { models, loading, error };
};

/** Group a model list by provider display name, preserving catalog order. */
export const groupByProvider = (models) => {
  const groups = {};
  for (const m of models) {
    (groups[m.provider] = groups[m.provider] || []).push(m);
  }
  return groups;
};

const fmt = (n) => (n < 1 ? n.toFixed(3) : n.toFixed(2));

/** Real per-1M-token price label, e.g. "$3.13 / $12.50 /1M". */
export const formatPrice = (m) =>
  m && typeof m.input === 'number' ? `$${fmt(m.input)} / $${fmt(m.output)} /1M` : '';
